/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */

import React, { useEffect } from 'react';
import { func, object } from 'prop-types';
import mbpLogger from '@module-logger/mbp';
import {
    Box, Collapse, makeStyles,
} from '@material-ui/core';
import ReactMarkdown from 'react-markdown';
import { useSelector } from 'react-redux';
import { parse, format } from 'date-fns';

import noop from '../../../../../helpers/noop';
import StyledButton from '../../../../EnterpriseDesignSystem/Button/StyledButton';
import useGetRecentOrderStatus from '../../../../../helpers/Personalization/useGetRecentOrderStatus';
import useClickStreamCustomEventsTracking from '../../../../../helpers/Personalization/useClickstreamCustomEventsTracking';
import { getIsAuthenticatedStatus } from '../../../../../../state/ducks/Member/ducks/Common/Common-Selectors';
import parseFontFamilyExtension from '../../../../../helpers/contentstack/parseFontFamilyExtension';

const useStyles = makeStyles((theme) => ({
    container: (props) => ({
        backgroundColor: props?.backgroundColor || '#E8F4E8',
        margin: props?.margin,
        maxWidth: props?.maxWidth,
        marginLeft: 'auto',
        marginRight: 'auto',
        padding: props?.padding || '8px 16px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    }),
    img: {
        width: '30px',
        height: '32px',
        borderRadius: '3px',
        flexShrink: 0,
        overflow: 'hidden',
        marginRight: '8px',
        [theme.breakpoints.down(600)]: {
            marginRight: '11px',
        },

    },
    label: (styles) => ({
        fontSize: styles?.fontSize || '14px',
        lineHeight: '19px',
        fontFamily: styles?.fontFamily || 'museo-sans, "Area Variable"',
        color: styles?.color || '#201648',
        fontWeight: styles?.fontWeight,
        [theme.breakpoints.up(600)]: {
            '& strong': {
                marginLeft: '8px',
            },
        },
    }),
    button: {
        marginLeft: '16px',
        marginTop: '-2px',
    },
}));

function RecentOrdersBanner({ salesforceResponse, setShowSkeleton }) {
    const content = salesforceResponse?.content;
    const customButton = content?.button;

    const cmsStyles = {
        backgroundColor: content?.background_color?.color || 'white',
        margin: content?.margin || '0px',
        maxWidth: content?.max_width || '100%',
        fontFamily: parseFontFamilyExtension(content?.main?.font_family),
        color: content?.main?.font_color?.color,
        fontSize: content?.main?.font_size,
        fontWeight: content?.main?.font_weight,
        padding: content?.padding,
    };

    const classes = useStyles(cmsStyles);
    const isAuthenticated = useSelector(getIsAuthenticatedStatus);

    useEffect(() => {
        setShowSkeleton(false);
    }, []);

    if (!isAuthenticated) return null;

    const trackClickThrough = useClickStreamCustomEventsTracking({
        salesforceResponse,
        isFireImpression: true,
        page: { type: 'home' },
    });

    const { loading, error, data } = useGetRecentOrderStatus({ daysInPast: content?.days_in_past, daysInFuture: content?.days_in_future });

    if (loading) {
        return null;
    }

    if (error) {
        mbpLogger.logError({
            function: 'RecentOrdersBanner',
            message: 'Error loading API data',
            appName: process.env.npm_package_name,
            module: 'PersonalizedContent',
            jsError: error,
        });
        return null;
    }

    const formatDate = (dateString) => {
        const cleanDateString = dateString.replace(/\.$/, '');
        // Parse into Date
        const parsedDate = parse(cleanDateString, 'yyyy-MM-dd HH:mm:ss', new Date());
        // Format as "August 12"
        const formattedDate = format(parsedDate, 'MMMM dd');
        return formattedDate;
    };

    const renderCopy = () => {
        const firstOrder = data?.recentOrderStatus?.recentOrders?.[0] || {};
        const isDelivered = firstOrder?.delivered;
        const copy = isDelivered ? content?.main?.delivered : content?.main?.arriving;
        const updatedCopy = copy.replace('{Recipient}', firstOrder?.recpFirstName).replace('{Date}', formatDate(firstOrder?.estimatedDeliveryDate));
        return updatedCopy;
    };

    if (data?.recentOrderStatus?.recentOrders?.length > 0) {
        const firstOrder = data?.recentOrderStatus?.recentOrders?.[0];

        return (
            <Collapse in timeout={1500}>
                <Box className={classes.container}>
                    {firstOrder?.productImageURL && (
                        <img className={classes.img} src={firstOrder.productImageURL} alt="product" />
                    )}
                    <ReactMarkdown source={renderCopy()} className={classes.label} />

                    <div className={classes.button}>
                        <StyledButton
                            label={customButton?.button?.link?.title}
                            link={customButton?.button?.link?.href}
                            variant={customButton?.button?.variant}
                            size={customButton?.button?.size}
                            handleOnClick={() => trackClickThrough()}
                            attributes={
                                {
                                    style: {
                                        fontFamily: parseFontFamilyExtension(customButton?.font_family),
                                        fontSize: customButton?.font_size,
                                        fontWeight: customButton?.font_weight,
                                    },
                                }
                            }
                        />
                    </div>

                </Box>
            </Collapse>
        );
    }

    return null;
}

RecentOrdersBanner.propTypes = {
    salesforceResponse: object.isRequired,
    setShowSkeleton: func,
};

RecentOrdersBanner.defaultProps = {
    setShowSkeleton: noop,
};

export default RecentOrdersBanner;
